// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import { NavLink, useNavigate } from 'react-router-dom'
import {
  LayoutDashboard, Heart, FolderOpen, Users, MessageSquare,
  Building2, Smartphone, ShieldCheck, Settings, Sparkles,
  LogOut, Leaf, CreditCard, Scale, BookOpen, Car, Briefcase } from 'lucide-react'
import { cn } from '@/lib/utils'
import { ROUTES, DOSSIER_OVERVIEW } from '@/lib/constants/routes'
import { useAuthStore } from '@/store'

interface NavItem {
  label:   string
  path:    string
  icon:    React.ReactNode
  badge?:  number
}

interface NavSection {
  title:  string
  items:  NavItem[]
}

const navSections: NavSection[] = [
  {
    title: '',
    items: [
      {
        label: 'Dashboard',
        path:  ROUTES.DASHBOARD,
        icon:  <LayoutDashboard className="h-4 w-4" />,
      },
    ],
  },
  {
    title: 'Mijn Voorbereiding',
    items: [
      { label: 'Persoonlijke gegevens', path: ROUTES.INTAKE,          icon: <Leaf className="h-4 w-4" /> },
      { label: 'Wensen',               path: ROUTES.WISHES,          icon: <Heart className="h-4 w-4" /> },
      { label: 'Medische wensen',      path: ROUTES.WISHES_MEDICAL,  icon: <Heart className="h-4 w-4 opacity-60" /> },
      { label: 'Uitvaartwensen',       path: ROUTES.WISHES_FUNERAL,  icon: <Heart className="h-4 w-4 opacity-60" /> },
      { label: 'Afscheid',             path: ROUTES.WISHES_FAREWELL, icon: <Heart className="h-4 w-4 opacity-60" /> },
      { label: 'Vertrouwenspersoon',   path: ROUTES.TRUSTED_PERSON,  icon: <Users className="h-4 w-4" /> },
      { label: 'Berichtenservice',     path: ROUTES.MESSAGES,        icon: <MessageSquare className="h-4 w-4" /> },
      { label: 'Documentwijzer',    path: ROUTES.DOCUMENTS,       icon: <FolderOpen className="h-4 w-4" /> },
      { label: 'Notariële wensen',    path: ROUTES.NOTARIAL,        icon: <Scale className="h-4 w-4" /> },
    ],
  },
  {
    title: 'Informatie',
    items: [
      { label: 'Financieel overzicht', path: ROUTES.FINANCIAL,     icon: <CreditCard className="h-4 w-4" /> },
      { label: 'Voertuigen & mobiliteit', path: ROUTES.VEHICLES,   icon: <Car className="h-4 w-4" /> },
      { label: 'Onderneming',          path: ROUTES.BUSINESS,      icon: <Briefcase className="h-4 w-4" /> },
      { label: 'Gemeente & overheid',  path: ROUTES.MUNICIPALITY,  icon: <Building2 className="h-4 w-4" /> },
      { label: 'Digitale nalatenschap',path: ROUTES.AI_GUIDE,     icon: <Smartphone className="h-4 w-4" /> },
    ],
  },
  {
    title: 'Beheer',
    items: [
      { label: 'Mijn dossiers',   path: DOSSIER_OVERVIEW,        icon: <BookOpen className="h-4 w-4" /> },
      { label: 'Controlecentrum', path: ROUTES.CONTROL_CENTER, icon: <ShieldCheck className="h-4 w-4" /> },
      { label: 'Instellingen',    path: ROUTES.SETTINGS,           icon: <Settings className="h-4 w-4" /> },
    ],
  },
]

interface SidebarProps {
  collapsed?: boolean
  onNavigate?: () => void
}

export function Sidebar({ collapsed = false, onNavigate }: SidebarProps) {
  const { clearAuth } = useAuthStore()
  const navigate      = useNavigate()

  function handleLogout() {
    clearAuth()
    navigate(ROUTES.LOGIN)
  }

  return (
    <aside
      className={cn(
        'flex flex-col h-full bg-white border-r border-sand-300',
        'transition-all duration-200',
        collapsed ? 'w-16' : 'w-64',
      )}
    >
      {/* Logo */}
      <div className="flex items-center gap-2.5 px-5 py-5 border-b border-sand-200">
        <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-moss flex-shrink-0">
          <Leaf className="h-4 w-4 text-white" />
        </div>
        {!collapsed && (
          <span className="text-base font-semibold text-ink">WilsApp</span>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 px-3 flex flex-col gap-1">
        {navSections.map((section, si) => (
          <div key={si} className={cn(si > 0 && 'mt-4')}>
            {!collapsed && section.title && (
              <p className="px-2.5 mb-1.5 text-xs font-medium text-ink-4 uppercase tracking-wider">
                {section.title}
              </p>
            )}
            {section.items.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                onClick={onNavigate}
                end={item.path === ROUTES.DASHBOARD || item.path === ROUTES.WISHES}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-2.5 rounded-xl px-2.5 py-2 text-sm',
                    'transition-all duration-150',
                    isActive
                      ? 'bg-sage-50 text-sage-700 font-medium'
                      : 'text-ink-3 hover:bg-sand-100 hover:text-ink-2',
                    collapsed && 'justify-center px-2',
                  )
                }
                title={collapsed ? item.label : undefined}
              >
                <span className="flex-shrink-0">{item.icon}</span>
                {!collapsed && <span className="truncate">{item.label}</span>}
                {!collapsed && item.badge !== undefined && (
                  <span className="ml-auto flex h-5 min-w-[20px] items-center justify-center rounded-full bg-sage-100 px-1.5 text-xs font-medium text-sage-700">
                    {item.badge}
                  </span>
                )}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      {/* AI Guide */}
      <div className="px-3 pb-2">
        <NavLink
          to={ROUTES.AI_GUIDE}
          className={({ isActive }) =>
            cn(
              'flex items-center gap-2.5 rounded-xl px-2.5 py-2.5 text-sm',
              'transition-all duration-150 border',
              isActive
                ? 'bg-sage-50 border-sage-200 text-sage-700 font-medium'
                : 'border-sand-200 text-ink-3 hover:bg-sage-50 hover:border-sage-200 hover:text-ink-2',
              collapsed && 'justify-center px-2',
            )
          }
          title={collapsed ? 'Digitale nalatenschap' : undefined}
        >
          <Sparkles className="h-4 w-4 flex-shrink-0 text-sage-500" />
          {!collapsed && <span>Digitale nalatenschap</span>}
        </NavLink>
      </div>

      {/* Logout */}
      <div className="px-3 pb-4 border-t border-sand-200 pt-2">
        <button
          onClick={handleLogout}
          className={cn(
            'flex items-center gap-2.5 rounded-xl px-2.5 py-2 text-sm w-full',
            'text-ink-4 hover:bg-sand-100 hover:text-ink-3',
            'transition-all duration-150',
            collapsed && 'justify-center px-2',
          )}
          title={collapsed ? 'Uitloggen' : undefined}
        >
          <LogOut className="h-4 w-4 flex-shrink-0" />
          {!collapsed && <span>Uitloggen</span>}
        </button>
      </div>
    </aside>
  )
}

// Mobile sidebar item for bottom nav
export function SidebarMobileItem({
  label, path, icon, isActive,
}: {
  label:    string
  path:     string
  icon:     React.ReactNode
  isActive: boolean
}) {
  return (
    <NavLink
      to={path}
      className={cn(
        'flex flex-col items-center gap-1 px-2 py-1.5',
        'transition-colors duration-150',
        isActive ? 'text-sage-600' : 'text-ink-4',
      )}
    >
      <span className="flex-shrink-0">{icon}</span>
      <span className="text-[10px] font-medium tracking-tight">{label}</span>
    </NavLink>
  )
}
